/**
 * Charakterbogen (5e 2014 / 2024). Die Spielwerte (Attribute, Fertigkeiten,
 * RK, TP-Maximum, Aktionen, Merkmale) liegen im Statblock; der Bogen ergänzt
 * im Frontmatter unter `sheet` alles, was sich während des Spiels ändert.
 */
import { sameJson } from './frontmatter.js';
import { ABILITY_KEYS, abilityMod, abilityScore, type AbilityKey, type Statblock } from './statblock.js';

export const COINS = ['cp', 'sp', 'ep', 'gp', 'pp'] as const;
export type Coin = (typeof COINS)[number];
export const HIT_DIE_SIZES = [6, 8, 10, 12] as const;

export interface SpellSlot {
  level: number;
  total: number;
  used: number;
}

/** Gegenstand im Inventar. Wert in Goldmünzen und Gewicht in kg je Stück. */
export interface InventoryItem {
  name: string;
  qty: number;
  value?: number;
  weight?: number;
}

export interface CharacterSheet {
  format?: '2014' | '2024';
  xp?: number;
  inspiration?: boolean;
  /** Aktuelle TP (Maximum aus dem Statblock). */
  hpCurrent?: number;
  hpTemp?: number;
  hitDie?: number;
  hitDiceUsed?: number;
  deathSaves?: { success: number; failure: number };
  /** Verstorben (wird mit dem Kampf-Tracker abgeglichen). */
  dead?: boolean;
  spellAbility?: AbilityKey;
  slots?: SpellSlot[];
  coins?: Partial<Record<Coin, number>>;
  proficiencies?: string;
  equipment?: string;
  inventory?: InventoryItem[];
  weaponMasteries?: string[];
}

/** Übungsbonus nach Charakterstufe (1–4: +2 … 17–20: +6). */
export function profFromLevel(level: number): number {
  const l = Math.min(20, Math.max(1, Math.floor(level)));
  return Math.ceil(l / 4) + 1;
}

const int = (v: unknown, min = 0, max = 99999): number | undefined => {
  const n = typeof v === 'number' ? v : typeof v === 'string' && v.trim() !== '' ? Number(v) : NaN;
  return Number.isFinite(n) ? Math.min(max, Math.max(min, Math.floor(n))) : undefined;
};

/** Nicht-negative Kommazahl (Gold, Gewicht), auf drei Stellen gerundet. */
const decimal = (v: unknown): number | undefined => {
  const n = typeof v === 'number' ? v : typeof v === 'string' && v.trim() !== '' ? Number(v.replace(',', '.')) : NaN;
  return Number.isFinite(n) && n >= 0 ? Math.round(n * 1000) / 1000 : undefined;
};

export function normalizeInventory(raw: unknown): InventoryItem[] {
  if (!Array.isArray(raw)) return [];
  return raw
    .filter((x): x is Record<string, unknown> => !!x && typeof x === 'object')
    .map((x) => {
      const item: InventoryItem = { name: typeof x.name === 'string' ? x.name.trim().slice(0, 200) : '', qty: int(x.qty, 0, 999999) ?? 1 };
      const value = decimal(x.value);
      if (value !== undefined) item.value = value;
      const weight = decimal(x.weight);
      if (weight !== undefined) item.weight = weight;
      return item;
    })
    .filter((x) => x.name !== '');
}

/** Summen über das Inventar (Anzahl × Wert bzw. Gewicht). */
export function inventoryTotals(items: readonly InventoryItem[]): { value: number; weight: number } {
  let value = 0;
  let weight = 0;
  for (const i of items) {
    value += (i.value ?? 0) * i.qty;
    weight += (i.weight ?? 0) * i.qty;
  }
  return { value: Math.round(value * 100) / 100, weight: Math.round(weight * 1000) / 1000 };
}

/** Liest den Bogen aus beliebigem JSON und entfernt Leer-/Standardwerte. */
export function normalizeSheet(raw: unknown): CharacterSheet {
  const r = (raw && typeof raw === 'object' && !Array.isArray(raw) ? raw : {}) as Record<string, unknown>;
  const out: CharacterSheet = {};
  if (r.format === '2024' || r.format === 2024) out.format = '2024';
  else if (r.format === '2014' || r.format === 2014) out.format = '2014';
  const xp = int(r.xp);
  if (xp) out.xp = xp;
  if (r.inspiration === true) out.inspiration = true;
  if (r.dead === true) out.dead = true;
  const hpc = int(r.hpCurrent);
  if (hpc !== undefined) out.hpCurrent = hpc;
  const hpt = int(r.hpTemp);
  if (hpt) out.hpTemp = hpt;
  const hd = int(r.hitDie, 4, 20);
  if (hd) out.hitDie = hd;
  const hdu = int(r.hitDiceUsed, 0, 40);
  if (hdu) out.hitDiceUsed = hdu;
  if (r.deathSaves && typeof r.deathSaves === 'object') {
    const d = r.deathSaves as Record<string, unknown>;
    const success = int(d.success, 0, 3) ?? 0;
    const failure = int(d.failure, 0, 3) ?? 0;
    if (success || failure) out.deathSaves = { success, failure };
  }
  if (typeof r.spellAbility === 'string' && (ABILITY_KEYS as readonly string[]).includes(r.spellAbility)) out.spellAbility = r.spellAbility as AbilityKey;
  if (Array.isArray(r.slots)) {
    const slots = r.slots
      .filter((s): s is Record<string, unknown> => !!s && typeof s === 'object')
      .map((s) => {
        const total = int(s.total, 0, 9) ?? 0;
        return { level: int(s.level, 1, 9) ?? 1, total, used: Math.min(total, int(s.used, 0, 9) ?? 0) };
      })
      .filter((s) => s.total > 0)
      .sort((a, b) => a.level - b.level);
    if (slots.length) out.slots = slots;
  }
  if (r.coins && typeof r.coins === 'object') {
    const coins: Partial<Record<Coin, number>> = {};
    for (const c of COINS) {
      const v = int((r.coins as Record<string, unknown>)[c], 0, 9999999);
      if (v) coins[c] = v;
    }
    if (Object.keys(coins).length) out.coins = coins;
  }
  for (const k of ['proficiencies', 'equipment'] as const) if (typeof r[k] === 'string' && (r[k] as string).trim()) out[k] = r[k] as string;
  const inventory = normalizeInventory(r.inventory);
  if (inventory.length) out.inventory = inventory;
  if (Array.isArray(r.weaponMasteries)) {
    const wm = r.weaponMasteries.filter((x): x is string => typeof x === 'string' && x.trim() !== '').map((x) => x.trim());
    if (wm.length) out.weaponMasteries = wm;
  }
  return out;
}

export function sheetOf(frontmatter: Record<string, unknown> | null | undefined): CharacterSheet {
  return normalizeSheet(frontmatter?.sheet);
}

/** Was sich bei Verstorbenen noch ändern darf: Inventar, Münzen, Ausrüstung (Leichnam plündern). */
const LOOTABLE = ['inventory', 'coins', 'equipment', 'dead'] as const;

function lockedPart(sheet: CharacterSheet): Record<string, unknown> {
  const out: Record<string, unknown> = { ...sheet };
  for (const k of LOOTABLE) delete out[k];
  return out;
}

/**
 * Verstorbene Charaktere/NSC sind eingefroren: Spielwerte (Statblock, TP, Zauberplätze …)
 * bleiben unverändert, solange sie verstorben sind. Gibt den Grund zurück oder null.
 */
export function deadLockViolation(before: Record<string, unknown>, after: Record<string, unknown>): 'sheet' | 'statblock' | null {
  if (!sheetOf(before).dead || !sheetOf(after).dead) return null;
  if (!sameJson(lockedPart(sheetOf(before)), lockedPart(sheetOf(after)))) return 'sheet';
  if (!sameJson(before.statblock ?? null, after.statblock ?? null)) return 'statblock';
  return null;
}

/** Zauber-SG und Zauberangriffsbonus. */
export function spellcasting(sb: Statblock, sheet: CharacterSheet, prof: number): { dc: number; attack: number } | null {
  if (!sheet.spellAbility) return null;
  const mod = abilityMod(abilityScore(sb, sheet.spellAbility));
  return { dc: 8 + prof + mod, attack: prof + mod };
}

/**
 * Bringt Statblock und Stufe eines Charakters zusammen: ohne Herausforderungsgrad
 * ergibt sich der Übungsbonus aus der Stufe.
 */
export function applyCharacterLevel(frontmatter: Record<string, unknown>): Record<string, unknown> {
  const level = typeof frontmatter.level === 'number' ? frontmatter.level : Number(frontmatter.level);
  const sb = frontmatter.statblock as Statblock | undefined;
  if (!sb || typeof sb !== 'object' || sb.cr || !Number.isFinite(level) || level < 1) return frontmatter;
  const prof = profFromLevel(level);
  if (sb.customProf === prof) return frontmatter;
  return { ...frontmatter, statblock: { ...sb, customProf: prof } };
}
